import { fetcher } from '@chipinside/fetcher'
import { Translate } from '@chipinside/translate'
import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { shallowEqual, useSelector } from 'react-redux'
import { persistReducer } from 'redux-persist'

import { loadPermissions } from '#/store/ducks/permissions'
import { endpoint } from '#/store/services/endpoints'
import { setAnalyticsUserInfo } from '#/store/services/Firebase/Analytics'
import { firebaseSignIn } from '#/store/services/Firebase/SignIn'
import { firebaseSignOut } from '#/store/services/Firebase/SignOut'
import storage from '#/utils/ReduxPersistStorage'
import { sanitizeError } from '#/utils/SanitizeError'
import tryCache from '#/utils/tryCache'
import ws from '#/utils/websocket'

import { loadApiVersion } from '../app'

const defaultState = {
  login: {
    status: null,
    error: null,
  },
  firebase: {
    firebaseToken: null,
    messagingToken: null,
    status: null,
    error: null,
  },
  logout: {
    status: null,
    error: null,
  },
  user: null,
  access: {
    status: null,
    error: null,
    userCompanies: [],
    userFarms: [],
  },

  help_pages: {
    error: null,
    list: null,
    expires: 0,
  },
  tour: {},
}

/**
 * @function loadHelpPages - get list of help pages
 * */
export const loadHelpPages = createAsyncThunk('auth/loadHelpPages', async (_, thunkAPI) => {
  try {
    const url = endpoint.database.help_pages.list()
    const { data: helpPages } = await fetcher({ controller: url })

    return helpPages
  } catch (e) {
    return thunkAPI.rejectWithValue({ message: e.message })
  }
})

export const login = createAsyncThunk('auth/login', async (_, thunkAPI) => {
  try {
    // get user info
    const controller = endpoint.user.info()
    const { data: user } = await fetcher({ controller })

    // get user permissions, await pois obrigatoriamente é necessário para funcionar
    await thunkAPI.dispatch(loadPermissions({ context: 'system' }))

    setAnalyticsUserInfo(user)

    // get help pages
    thunkAPI.dispatch(loadHelpPages())

    // set user's app language
    const shortLanguage = Translate.getShortLanguage(user.locale ?? 'pt_BR')
    document.documentElement.lang = shortLanguage
    Translate.setLanguage(shortLanguage)

    // fetch api version
    thunkAPI.dispatch(loadApiVersion())

    // check if user is farm and has active farms
    user.isSuspended = !user.has_company && user.active_farm_count === 0

    return { user }
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: sanitizeError(error) })
  }
})

export const getFirebaseToken = createAsyncThunk('auth/getFirebaseToken', async (_, thunkAPI) => {
  try {
    const { firebaseToken, messagingToken } = await firebaseSignIn()
    if (messagingToken) {
      /* Registering the device token with the backend. */
      fetcher({
        method: 'post',
        controller: endpoint.auth.firebase_register_device(),
        params: {
          firebase_token: messagingToken,
        },
      })
    }
    return { firebaseToken, messagingToken }
  } catch (e) {
    return thunkAPI.rejectWithValue({ message: e.message })
  }
})

export const resetFirebaseToken = createAsyncThunk(
  'auth/resetFirebaseToken',
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState()
      await firebaseSignOut(state.auth.firebase.messagingToken)

      thunkAPI.dispatch(resetFirebase())
    } catch (e) {
      return thunkAPI.rejectWithValue({ message: e.message })
    }
  },
)

export const logout = createAsyncThunk('auth/logout', async (_, thunkAPI) => {
  try {
    const state = thunkAPI.getState()

    // disconnect from echo
    ws.disconnect()

    // logout from firebase
    await firebaseSignOut(state.auth.firebase.messagingToken)
  } catch (e) {
    return thunkAPI.rejectWithValue({ message: e.message })
  } finally {
    // clears all localStorage
    localStorage.clear()
  }
})

export const viewTour = createAction('auth/viewTour')
export const setOnboardingViewed = createAction('auth/setOnboardingViewed')

export const authSlice = createSlice({
  name: 'auth',
  initialState: defaultState,
  reducers: {
    resetFirebase(state) {
      state.firebase = defaultState.firebase
    },
    resetLogin(state) {
      state.login = defaultState.login
      state.user = defaultState.user
      state.access = defaultState.access
    },
  },
  extraReducers: builder => {
    builder
      // LOGIN
      .addCase(login.pending, state => {
        state.login.status = 'started'
      })
      .addCase(login.fulfilled, (state, action) => {
        state.login.status = 'done'
        state.user = action.payload?.user
      })
      .addCase(login.rejected, (state, { payload }) => {
        state.login.status = 'failed'
        throw payload.error
      })
      // RESET TOKEN FIREBASE
      .addCase(resetFirebaseToken.fulfilled, state => {
        state.firebase.status = 'done'
      })
      .addCase(resetFirebaseToken.rejected, (state, action) => {
        state.firebase.status = 'failed'
        state.firebase.error = action.payload?.message
      })
      // TOKEN FIREBASE
      .addCase(getFirebaseToken.pending, state => {
        state.firebase.error = null
        state.firebase.status = 'started'
      })
      .addCase(getFirebaseToken.fulfilled, (state, action) => {
        state.firebase.firebaseToken = action.payload?.firebaseToken
        state.firebase.messagingToken = action.payload?.messagingToken
        state.firebase.status = 'done'
      })
      .addCase(getFirebaseToken.rejected, (state, action) => {
        state.firebase.status = 'failed'
        state.firebase.error = action.payload?.message
      })
      // LOGOUT
      .addCase(logout.pending, state => {
        state.logout.status = 'started'
      })
      .addCase(logout.fulfilled, state => {
        state.login = defaultState.login
        state.firebase = defaultState.firebase
        state.logout = defaultState.logout
        state.user = defaultState.user

        window.location.replace(`${import.meta.env.VITE_AUTH_URL}/logout`)
      })
      .addCase(logout.rejected, (state, action) => {
        state.logout.status = 'failed'
        state.logout.error = action.payload?.message
      })
      //HELP PAGES
      .addCase(loadHelpPages.fulfilled, (state, action) => {
        state.help_pages.list = action.payload
        state.help_pages.expires = tryCache.expiresIn(15)
        state.help_pages.error = null
      })
      .addCase(loadHelpPages.rejected, (state, action) => {
        state.help_pages.error = action.payload?.message
      })
      .addCase(viewTour, (state, action) => {
        const id = action.payload
        state.tour[id] = true
      })
      .addCase(setOnboardingViewed, state => {
        state.user.has_seen_onboarding = true
      })
  },
  selectors: {
    getLoginInfo: state => state?.user ?? null,
    getFirebaseInfo: state => state?.firebase ?? null,
    getUserInfo: state => state?.user ?? {},
    getUserSlug: state => state?.user?.slug ?? null,
    getHelpPages: state => state?.help_pages?.list ?? [],
    getUserTour: state => state?.tour ?? {},
    getUserLocale: state => state?.user?.locale ?? null,
  },
})

export const {
  actions: { resetLogin, resetFirebase },
  selectors: {
    getLoginInfo,
    getFirebaseInfo,
    getUserInfo,
    getHelpPages,
    getUserLocale,
    getUserTour,
    getUserSlug,
  },
} = authSlice

export const useHelpPages = () => useSelector(getHelpPages, shallowEqual)

export const useUserTour = () => useSelector(getUserTour, shallowEqual)
export const useUserInfo = () => useSelector(getUserInfo, shallowEqual)
export const useUserSlug = () => useSelector(getUserSlug)
export const useUserLocale = () => useSelector(getUserLocale, shallowEqual)

const persistConfig = {
  key: 'firebase',
  storage,
  whitelist: ['firebase', 'tour'],
}

export default persistReducer(persistConfig, authSlice.reducer)
